import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { t } from '../translations';
import { router, resolveRouterPath } from '../router';

@customElement('bottom-tabs')
export class BottomTabs extends LitElement {
  @state() private activeTab = 'home';

  static styles = css`
    :host {
      display: block;
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      background: #0D1726;
      box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.2);
      z-index: 1000;
      height: 60px;
      /* iOS safe area handling with fallbacks */
      padding-bottom: env(safe-area-inset-bottom, 0px);
      padding-bottom: constant(safe-area-inset-bottom, 0px);
      /* Set CSS custom property for total height including safe area */
      --bottom-tabs-total-height: calc(60px + var(--safe-area-inset-bottom, 0px));
    }

    .tabs {
      display: flex;
      justify-content: space-around;
      align-items: center;
      height: 100%;
    }

    .tab {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 0.5rem;
      color: #ffffff;
      text-decoration: none;
      font-size: 0.8rem;
      transition: color 0.3s ease;
      cursor: pointer;
      user-select: none;
      opacity: 0.7;
    }

    .tab.active {
      color: #ffffff;
      opacity: 1;
    }

    .tab-icon {
      font-size: 1.5rem;
      margin-bottom: 0.25rem;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .tab-icon img {
      width: 24px;
      height: 24px;
      filter: brightness(0) invert(1);
      opacity: 0.7;
      transition: opacity 0.3s ease;
      display: block;
      /* Ensure images stay loaded */
      object-fit: contain;
      image-rendering: -webkit-optimize-contrast;
    }

    .tab-icon img.error {
      display: none;
    }

    .tab-icon .fallback {
      display: none;
      font-size: 24px;
      opacity: 0.7;
      transition: opacity 0.3s ease;
    }

    .tab-icon img.error + .fallback {
      display: block;
    }

    .tab.active .tab-icon img {
      opacity: 1;
    }

    .tab.active .tab-icon .fallback {
      opacity: 1;
    }
  `;

  private handleTabClick(tab: string) {
    this.activeTab = tab;

    // Navigate client-side through the router instead of a full page reload.
    switch (tab) {
      case 'home':
        router.navigate(resolveRouterPath('nowcast'));
        break;
      case 'map':
        router.navigate(resolveRouterPath());
        break;
      case 'aurora':
        router.navigate(resolveRouterPath('aurora'));
        break;
      case 'settings':
        router.navigate(resolveRouterPath('settings'));
        break;
    }
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('language-changed', this._onLanguageChanged);
    window.addEventListener('popstate', this._onRouteChanged);
    // Set active tab based on current path
    this._updateActiveTabFromPath();
    // Preload icons to prevent disappearing on iOS
    this._preloadIcons();
  }

  disconnectedCallback() {
    window.removeEventListener('language-changed', this._onLanguageChanged);
    window.removeEventListener('popstate', this._onRouteChanged);
    super.disconnectedCallback();
  }

  private _onLanguageChanged = () => {
    this.requestUpdate();
  };

  private _onRouteChanged = () => {
    this._updateActiveTabFromPath();
  };

  private _updateActiveTabFromPath() {
    const path = window.location.pathname;
    if (path === '/' || path === '') {
      this.activeTab = 'map';
    } else if (path.startsWith('/nowcast')) {
      this.activeTab = 'home';
    } else if (path.startsWith('/aurora')) {
      this.activeTab = 'aurora';
    } else if (path.startsWith('/settings')) {
      this.activeTab = 'settings';
    }
  }

  private _preloadIcons() {
    const icons = [
      '/assets/icons/icon_globe.png',
      '/assets/icons/icon_nowcast2.png', 
      '/assets/icons/icon_aurora.png',
      '/assets/icons/icon_settings.png'
    ];
    
    icons.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }

  private handleImageError(event: Event) {
    const img = event.target as HTMLImageElement;
    img.classList.add('error');
  }

  render() {
    return html`
      <div class="tabs">
        <a class="tab ${this.activeTab === 'map' ? 'active' : ''}"
           @click=${() => this.handleTabClick('map')}>
          <span class="tab-icon">
            <img src="/assets/icons/icon_globe.png" alt="Map" @error=${this.handleImageError}>
            <span class="fallback">🗺️</span>
          </span>
          <span>${t('map')}</span>
        </a>
        <a class="tab ${this.activeTab === 'home' ? 'active' : ''}"
           @click=${() => this.handleTabClick('home')}>
          <span class="tab-icon">
            <img src="/assets/icons/icon_nowcast2.png" alt="Home" @error=${this.handleImageError}>
            <span class="fallback">🌌</span>
          </span>
          <span>${t('nowcast')}</span>
        </a>
        <a class="tab ${this.activeTab === 'aurora' ? 'active' : ''}"
           @click=${() => this.handleTabClick('aurora')}>
          <span class="tab-icon">
            <img src="/assets/icons/icon_aurora.png" alt="Aurora" @error=${this.handleImageError}>
            <span class="fallback">✨</span>
          </span>
          <span>${t('aurora')}</span>
        </a>
        <a class="tab ${this.activeTab === 'settings' ? 'active' : ''}"
           @click=${() => this.handleTabClick('settings')}>
          <span class="tab-icon">
            <img src="/assets/icons/icon_settings.png" alt="Settings" @error=${this.handleImageError}>
            <span class="fallback">⚙️</span>
          </span>
          <span>${t('settings')}</span>
        </a>
      </div>
    `;
  }
}